/**
 * Otto Token Theme Values
 *
 * Per-theme values for Otto extension tokens (ottoTokens.ts).
 * These CSS custom properties are injected by the FluentProvider wrapper.
 *
 * Each theme object maps CSS custom property names → values.
 * Add new token values here when extending ottoTokens.ts.
 */

import type { CSSProperties } from 'react';

/**
 * CSS custom properties record type.
 * Uses index signature to allow `--otto-*` property names.
 */
type OttoTokenValues = Record<string, string> & CSSProperties;

// ─── Theme-Invariant Values ─────────────────────────────────────────
//
// Tokens whose value does not depend on the active color scheme (sizes,
// fixed dimensions, etc.) live here once and are spread into both theme
// objects below. Add to this object when introducing a new token that
// would otherwise be identical across dark and light.

const ottoInvariantTokenValues = {
  // Settings modal
  '--otto-settings-nav-icon-size': '18px',
  // Global nav palette (⌘K) row icons — sized to match sidebar glyphs
  '--otto-palette-row-icon-size': '18px',
  // Cancelled status — pinned to Fluent orange.shade20 in both themes so
  // white badge text keeps WCAG AA contrast (Fluent's theme-flipping
  // palette tokens would fail AA in one theme).
  '--otto-status-cancelled': '#bc4b09',
} as const;

// ─── Dark Theme Values ──────────────────────────────────────────────

export const ottoDarkTokenValues: OttoTokenValues = {
  ...ottoInvariantTokenValues,
  // Canvas
  '--otto-canvas-bg': '#0B0E14',
  '--otto-canvas-grid': 'rgba(255, 255, 255, 0.04)',
  '--otto-canvas-selection-stroke': '#5B6CF0',
  '--otto-canvas-selection-fill': 'rgba(91, 108, 240, 0.08)',
  '--otto-canvas-minimap-note': 'rgba(200, 200, 200, 0.3)',
  '--otto-canvas-minimap-mask': 'rgba(0, 0, 0, 0.1)',

  // Node accents
  '--otto-node-accent-trigger': '#6bb700',
  '--otto-node-accent-action': '#0078d4',
  '--otto-node-accent-agent': '#8764b8',
  '--otto-node-accent-control-flow': '#c239b3',
  '--otto-node-accent-tool': '#858585',
  '--otto-node-accent-model': '#00a5af',

  // Note node
  '--otto-note-bg': '#FFF9C4',
  '--otto-note-fg': '#000000',
  '--otto-note-border-selected':
    '0 0 0 2px #fff, 0 0 0 4px rgba(0, 0, 0, 0.2)',

  // Overlay
  '--otto-overlay-backdrop': 'rgba(0, 0, 0, 0.4)',

  // Code editor chrome (dark)
  '--otto-editor-active-line': 'rgba(255, 255, 255, 0.05)',
  '--otto-editor-selection': 'rgba(38, 79, 120, 0.5)',
  '--otto-editor-selection-focused': 'rgba(38, 79, 120, 0.7)',
  '--otto-editor-search-match': 'rgba(255, 213, 0, 0.3)',
  '--otto-editor-search-match-selected': 'rgba(255, 150, 0, 0.5)',

  // Code editor syntax (dark — VS Code dark+ inspired)
  '--otto-code-string': '#ce9178',
  '--otto-code-number': '#b5cea8',
  '--otto-code-boolean': '#569cd6',
  '--otto-code-null': '#569cd6',
  '--otto-code-property-name': '#9cdcfe',
  '--otto-code-punctuation': '#d4d4d4',
  '--otto-code-keyword': '#569cd6',
  '--otto-code-control-keyword': '#c586c0',
  '--otto-code-function': '#dcdcaa',
  '--otto-code-variable': '#9cdcfe',
  '--otto-code-definition': '#4fc1ff',
  '--otto-code-comment': '#6a9955',
  '--otto-code-operator': '#d4d4d4',
  '--otto-code-type-name': '#4ec9b0',
  '--otto-code-regexp': '#d16969',

  // Status
  '--otto-status-succeeded': '#1ea41e',
  '--otto-status-failed': '#e8505e',
  '--otto-status-running': '#3290dc',
  '--otto-status-waiting': '#e06614',
  '--otto-status-skipped': '#8a8886',
  '--otto-status-timed-out': '#e8505e',
  '--otto-status-pending': '#8a8886',

  // Agent variant node fills (dark) — solid opaque colors so the
  // hexagon border technique (background-as-border + ::before fill) works.
  '--otto-agent-native-badge-bg': '#1B2E52',
  '--otto-agent-native-badge-fg': '#93C5FD',
  '--otto-agent-foundry-badge-bg': '#2A1D45',
  '--otto-agent-foundry-badge-fg': '#C7B3F0',
  '--otto-agent-coding-badge-bg': '#152F22',
  '--otto-agent-coding-badge-fg': '#7DD3A3',
  '--otto-agent-hosted-badge-bg': '#3D2B14',
  '--otto-agent-hosted-badge-fg': '#F9C97C',

  // Agent variant saturated brand colors — used as the icon tile
  // background in the operation picker, where the tile sits on a
  // neutral surface and benefits from a stronger fill.
  '--otto-agent-native-brand': '#2563EB',
  '--otto-agent-foundry-brand': '#8B5CF6',
  '--otto-agent-coding-brand': '#0E7C61',
  '--otto-agent-hosted-brand': '#D97706',

  // ─── Marketing landing page (dark) ─────────────────────────────────
  '--otto-landing-hero-ambient':
    'radial-gradient(ellipse at top, rgba(91, 140, 255, 0.25), transparent 60%), ' +
    'radial-gradient(ellipse at bottom right, rgba(74, 215, 255, 0.15), transparent 50%)',
  '--otto-landing-primary-gradient':
    'linear-gradient(135deg, #5B8CFF 0%, #4AD7FF 100%)',
  '--otto-landing-scenario-bg':
    'linear-gradient(135deg, rgba(91, 140, 255, 0.08), rgba(74, 215, 255, 0.04))',
  '--otto-landing-final-cta-bg':
    'linear-gradient(135deg, rgba(91, 140, 255, 0.15), rgba(74, 215, 255, 0.08))',
  '--otto-landing-card-bg': '#161824',
  '--otto-landing-dim-text': '#8A91A4',
  '--otto-landing-accent-text-cool': '#4AD7FF',
  '--otto-landing-approval-icon-ok': 'rgba(56, 211, 153, 0.18)',
  '--otto-landing-approval-icon-warn': 'rgba(255, 122, 89, 0.18)',
  '--otto-landing-approval-icon-idle': 'rgba(255, 255, 255, 0.06)',
  '--otto-landing-blocked-gradient':
    'linear-gradient(90deg, #FF7A59 0%, #FF5277 100%)',
  '--otto-landing-accent-border': 'rgba(91, 140, 255, 0.55)',
  '--otto-landing-primary-gradient-fg': '#0A0A0F',
};

// ─── Light Theme Values ─────────────────────────────────────────────

export const ottoLightTokenValues: OttoTokenValues = {
  ...ottoInvariantTokenValues,
  // Canvas
  '--otto-canvas-bg': '#FAFAFA',
  '--otto-canvas-grid': 'rgba(0, 0, 0, 0.06)',
  '--otto-canvas-selection-stroke': '#5B6CF0',
  '--otto-canvas-selection-fill': 'rgba(91, 108, 240, 0.08)',
  '--otto-canvas-minimap-note': 'rgba(200, 200, 200, 0.4)',
  '--otto-canvas-minimap-mask': 'rgba(0, 0, 0, 0.06)',

  // Node accents (same hue, adjusted for light backgrounds)
  '--otto-node-accent-trigger': '#498205',
  '--otto-node-accent-action': '#0078d4',
  '--otto-node-accent-agent': '#7160a0',
  '--otto-node-accent-control-flow': '#b4009e',
  '--otto-node-accent-tool': '#858585',
  '--otto-node-accent-model': '#038387',

  // Note node
  '--otto-note-bg': '#FFF9C4',
  '--otto-note-fg': '#000000',
  '--otto-note-border-selected':
    '0 0 0 2px #fff, 0 0 0 4px rgba(0, 0, 0, 0.2)',

  // Overlay
  '--otto-overlay-backdrop': 'rgba(0, 0, 0, 0.3)',

  // Code editor chrome (light)
  '--otto-editor-active-line': 'rgba(0, 0, 0, 0.04)',
  '--otto-editor-selection': 'rgba(173, 214, 255, 0.5)',
  '--otto-editor-selection-focused': 'rgba(173, 214, 255, 0.7)',
  '--otto-editor-search-match': 'rgba(255, 213, 0, 0.4)',
  '--otto-editor-search-match-selected': 'rgba(255, 150, 0, 0.6)',

  // Code editor syntax (light — VS Code light+ inspired)
  '--otto-code-string': '#a31515',
  '--otto-code-number': '#047a42',
  '--otto-code-boolean': '#0000ff',
  '--otto-code-null': '#0000ff',
  '--otto-code-property-name': '#0451a5',
  '--otto-code-punctuation': '#000000',
  '--otto-code-keyword': '#0000ff',
  '--otto-code-control-keyword': '#af00db',
  '--otto-code-function': '#795e26',
  '--otto-code-variable': '#001080',
  '--otto-code-definition': '#0070c1',
  '--otto-code-comment': '#008000',
  '--otto-code-operator': '#000000',
  '--otto-code-type-name': '#1f6e87',
  '--otto-code-regexp': '#811f3f',

  // Status
  '--otto-status-succeeded': '#0e7a0d',
  '--otto-status-failed': '#a4262c',
  '--otto-status-running': '#005faa',
  '--otto-status-waiting': '#ab4310',
  '--otto-status-skipped': '#6b6b6b',
  '--otto-status-timed-out': '#a4262c',
  '--otto-status-pending': '#6b6b6b',

  // Agent variant pastel badges (light) — richer tints over light surfaces
  '--otto-agent-native-badge-bg': '#CFDDFC',
  '--otto-agent-native-badge-fg': '#1E40AF',
  '--otto-agent-foundry-badge-bg': '#E4D8F2',
  '--otto-agent-foundry-badge-fg': '#5C4087',
  '--otto-agent-coding-badge-bg': '#D2ECDB',
  '--otto-agent-coding-badge-fg': '#0E6C45',
  '--otto-agent-hosted-badge-bg': '#FDE8C8',
  '--otto-agent-hosted-badge-fg': '#92400E',

  // Agent variant saturated brand colors (light) — same identity hues
  // as dark; brand colors are theme-invariant.
  '--otto-agent-native-brand': '#2563EB',
  '--otto-agent-foundry-brand': '#8B5CF6',
  '--otto-agent-coding-brand': '#0E7C61',
  '--otto-agent-hosted-brand': '#D97706',

  // ─── Marketing landing page (light) ────────────────────────────────
  // Light theme stays brand-aligned but darkens the gradient endpoints
  // so white text would still pass AA — though we never use white text
  // on the gradient. The static `landing-primary-gradient-fg` is the
  // primary CTA text colour: always near-black so contrast holds in
  // both themes regardless of the gradient endpoint.
  '--otto-landing-hero-ambient':
    'radial-gradient(ellipse at top, rgba(91, 140, 255, 0.18), transparent 60%), ' +
    'radial-gradient(ellipse at bottom right, rgba(74, 215, 255, 0.10), transparent 50%)',
  '--otto-landing-primary-gradient':
    'linear-gradient(135deg, #1857C6 0%, #1668B0 100%)',
  '--otto-landing-scenario-bg':
    'linear-gradient(135deg, rgba(45, 91, 255, 0.06), rgba(11, 165, 217, 0.04))',
  '--otto-landing-final-cta-bg':
    'linear-gradient(135deg, rgba(45, 91, 255, 0.10), rgba(11, 165, 217, 0.06))',
  '--otto-landing-card-bg': '#F5F7FB',
  '--otto-landing-dim-text': '#586073',
  '--otto-landing-accent-text-cool': '#0F5E94',
  '--otto-landing-approval-icon-ok': 'rgba(14, 122, 13, 0.14)',
  '--otto-landing-approval-icon-warn': 'rgba(171, 67, 16, 0.14)',
  '--otto-landing-approval-icon-idle': 'rgba(0, 0, 0, 0.05)',
  '--otto-landing-blocked-gradient':
    'linear-gradient(90deg, #D9531E 0%, #B5193F 100%)',
  '--otto-landing-accent-border': 'rgba(45, 91, 255, 0.5)',
  '--otto-landing-primary-gradient-fg': '#0A0A0F',
};
